import { PlansFundRoleByLumpsumAction, PlansScreen } from '../../../../../analytics/plansEvents'
import {
  trackPlansLumpsumFundTileOpen,
  trackPlansLumpsumFundTilePrimaryCta
} from '../../../../../analytics/plansTrack'
import { LumpsumRecommendationActionType } from '../../../../api/common.types'
import { usePlanBuyAction } from '../../../../../execution/buy/usePlanBuyAction'
import { usePlanSellAction } from '../../../../../execution/sell/usePlanSellAction'
import type { LumpsumAllocationBuyPlanRecommendationDetails } from '../../../api/allocationRecommendation.types'
import { openRecommendationFundDetail } from '../../../open/planRecommendationOpen'
import {
  buildPlanRecommendationFundActionLabel,
  PlanRecommendationFundTile,
  RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS
} from '../../../shared/funds/PlanRecommendationFundTile'
import { RecommendationCounterFundsSection } from '../../../shared/funds/RecommendationCounterFundsSection'
import { RecommendationFundSingleSection } from '../../../shared/funds/RecommendationFundSingleSection'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { AllocationPlanImportantNote } from '../../important-note/AllocationPlanImportantNote'

type AllocationBuyPlanRecommendationHeaderProps = {
  planDetails: LumpsumAllocationBuyPlanRecommendationDetails | null
  entryIsin: string
  selectedSellIndex: number
  onSelectSellIndex: (selectedIndex: number) => void
  transactionCta: unknown
}

export function AllocationBuyPlanRecommendationHeader({
  planDetails,
  entryIsin,
  selectedSellIndex,
  onSelectSellIndex,
  transactionCta
}: AllocationBuyPlanRecommendationHeaderProps) {
  const { onSell } = usePlanSellAction({ transactionCta, isRecommendationSheetSource: true })
  const { onBuy } = usePlanBuyAction({ transactionCta, isRecommendationSheetSource: true })
  const buyPlanDetails = planDetails
  const isEntryActionCompleted = buyPlanDetails?.executed === true
  const fundsToBeSold = buyPlanDetails?.fundsToBeSold ?? []
  const buyFundId = buyPlanDetails?.isin ?? entryIsin ?? 'buy-current'
  const buyRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: buyPlanDetails?.totalTransferAmount ?? 0 })
  const screenName = PlansScreen.LumpsumAllocationRecommendation

  return (
    <div className="pt-9 pb-4">
      <RecommendationFundSingleSection
        title="Buy"
        type={LumpsumRecommendationActionType.BUY}
        highlightIsin={isEntryActionCompleted ? '' : entryIsin}
        fundId={buyFundId}
      >
        <PlanRecommendationFundTile
          mfLogoUrl={buyPlanDetails?.mfLogoUrl ?? ''}
          fundName={buyPlanDetails?.fundName ?? ''}
          fundNature={buyPlanDetails?.fundNature ?? ''}
          fundCategory={buyPlanDetails?.fundCategory ?? ''}
          powerRating={buyPlanDetails?.powerRating ?? ''}
          powerRank={buyPlanDetails?.powerRank ?? null}
          purchaseMode={buyPlanDetails?.purchaseMode ?? ''}
          fundIsin={buyFundId}
          widthClassName={RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS}
          onCardClick={() => {
            trackPlansLumpsumFundTileOpen({
              screenName,
              fundRole: PlansFundRoleByLumpsumAction[LumpsumRecommendationActionType.BUY],
              fundName: buyPlanDetails?.fundName ?? '',
              fundIsin: buyFundId
            })
            openRecommendationFundDetail(buyFundId)
          }}
          recommendationAction={{
            action: LumpsumRecommendationActionType.BUY,
            recommendationFormattedAmount: buyRecommendationFormattedAmount,
            isCompleted: isEntryActionCompleted,
            onActionClick: () => {
              trackPlansLumpsumFundTilePrimaryCta({
                screenName,
                buttonLabel: buildPlanRecommendationFundActionLabel(
                  LumpsumRecommendationActionType.BUY,
                  buyRecommendationFormattedAmount
                ),
                fundName: buyPlanDetails?.fundName ?? '',
                fundIsin: buyFundId
              })
              void onBuy({
                isin: buyFundId,
                fundName: buyPlanDetails?.fundName ?? '',
                amount: buyRecommendationFormattedAmount.value
              })
            }
          }}
        />
      </RecommendationFundSingleSection>
      <RecommendationCounterFundsSection
        funds={fundsToBeSold}
        titleSingle="From Selling"
        titleMulti={`By Selling ${fundsToBeSold.length} funds`}
        type={LumpsumRecommendationActionType.SELL}
        onSelect={onSelectSellIndex}
        sectionClassName="pt-8"
        renderFundTile={(fund, widthClassName) => {
          const fundIsin = fund?.isin ?? ''
          const sellRecommendationFormattedAmount = showAsFormattedRupeeAmount({ rawAmount: fund?.sellAmount ?? 0 })
          return (
            <PlanRecommendationFundTile
              mfLogoUrl={fund?.mfLogoUrl ?? ''}
              fundName={fund?.fundName ?? ''}
              fundNature={fund?.fundNature ?? ''}
              fundCategory={fund?.fundCategory ?? ''}
              powerRating={fund?.powerRating ?? ''}
              powerRank={fund?.powerRank ?? null}
              purchaseMode={fund?.purchaseMode ?? ''}
              fundIsin={fundIsin}
              widthClassName={widthClassName}
              onCardClick={() => {
                trackPlansLumpsumFundTileOpen({
                  screenName,
                  fundRole: PlansFundRoleByLumpsumAction[LumpsumRecommendationActionType.SELL],
                  fundName: fund?.fundName ?? '',
                  fundIsin
                })
                openRecommendationFundDetail(fundIsin)
              }}
              recommendationAction={{
                action: LumpsumRecommendationActionType.SELL,
                recommendationFormattedAmount: sellRecommendationFormattedAmount,
                isCompleted: fund?.executed === true,
                onActionClick: () => {
                  trackPlansLumpsumFundTilePrimaryCta({
                    screenName,
                    buttonLabel: buildPlanRecommendationFundActionLabel(
                      LumpsumRecommendationActionType.SELL,
                      sellRecommendationFormattedAmount
                    ),
                    fundName: fund?.fundName ?? '',
                    fundIsin
                  })
                  void onSell({
                    isin: fundIsin,
                    fundName: fund?.fundName ?? '',
                    amount: sellRecommendationFormattedAmount.value,
                    currentValue: fund?.taxData?.currentValue ?? 0
                  })
                }
              }}
            />
          )
        }}
      />
      <AllocationPlanImportantNote importantNotes={buyPlanDetails?.importantNotes ?? null} />
    </div>
  )
}
